import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, type AdminUserRow } from '../lib/api';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

type Role = 'admin' | 'sbu' | 'finance' | 'viewer';
type EditableStatus = 'active' | 'disabled';

const ERROR_MESSAGES: Record<string, string> = {
  cannot_disable_self: 'Bạn không thể vô hiệu hoá tài khoản của chính mình.',
  cannot_demote_self: 'Bạn không thể hạ quyền tài khoản của chính mình.',
  cannot_delete_self: 'Bạn không thể xoá tài khoản của chính mình.',
  last_admin: 'Phải còn ít nhất 1 admin đang hoạt động. Vui lòng tạo/kích hoạt admin khác trước.',
};

export function AdminUserEditPage() {
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: me } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () =>
      fetch('/api/auth/me', { credentials: 'include' }).then(
        (r) =>
          r.json() as Promise<{
            authenticated: boolean;
            user?: { id: number; email: string; name: string; role: string };
          }>,
      ),
  });
  const isSelf = me?.user?.id === id;

  const { data: existing, isLoading } = useQuery<AdminUserRow>({
    queryKey: ['admin-user', id],
    queryFn: () => api.fetchUser(id),
    enabled: Number.isFinite(id),
  });

  const { data: deptsResp } = useQuery({
    queryKey: ['admin-departments'],
    queryFn: () => api.listDepartments(),
  });

  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [status, setStatus] = useState<EditableStatus>('active');
  const [deptIds, setDeptIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setRole(existing.role);
      setStatus(existing.status === 'disabled' ? 'disabled' : 'active');
      setDeptIds(existing.departments.map((d) => d.id));
    }
  }, [existing]);

  const save = useMutation({
    mutationFn: () => api.updateUser(id, { name, role, status, departmentIds: deptIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', id] });
      toast.success('Đã lưu thay đổi');
      navigate('/admin/users');
    },
    onError: (err: Error) => {
      const m = err.message.match(/\{"error":"([^"]+)"\}/);
      const code = m?.[1] ?? '';
      setError(ERROR_MESSAGES[code] ?? `Lỗi: ${err.message}`);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Đã xoá user');
      navigate('/admin/users');
    },
    onError: (err: Error) => {
      const m = err.message.match(/\{"error":"([^"]+)"\}/);
      const code = m?.[1] ?? '';
      setError(ERROR_MESSAGES[code] ?? `Lỗi: ${err.message}`);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  function toggleDept(d: number) {
    setDeptIds((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  return (
    <div className="space-y-4">
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} />
        Back to list
      </Link>

      <PageHeader title={`Edit ${existing ? existing.email : `#${id}`}`} />

      {isLoading && <Skeleton className="h-40 w-full" />}

      {existing && (
        <Card>
          <form onSubmit={onSubmit} className="space-y-3 p-4 text-sm">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={existing.email} disabled />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="role">
                  Role{' '}
                  {isSelf && <span className="text-xs">(disabled — bạn đang sửa chính mình)</span>}
                </Label>
                <Select value={role} onValueChange={(v) => setRole(v as Role)} disabled={isSelf}>
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">viewer</SelectItem>
                    <SelectItem value="finance">finance</SelectItem>
                    <SelectItem value="sbu">sbu</SelectItem>
                    <SelectItem value="admin">admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="status">
                  Status{' '}
                  {isSelf && <span className="text-xs">(disabled — bạn đang sửa chính mình)</span>}
                </Label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as EditableStatus)}
                  disabled={isSelf}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">active</SelectItem>
                    <SelectItem value="disabled">disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <fieldset className="block">
              <legend className="text-sm font-medium text-muted-foreground">Departments</legend>
              <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {deptsResp?.rows?.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-foreground">
                    <input
                      type="checkbox"
                      checked={deptIds.includes(d.id)}
                      onChange={() => toggleDept(d.id)}
                    />
                    <span className="font-mono text-xs text-muted-foreground">{d.code}</span>
                    <span className="truncate">{d.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {error && (
              <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <div>
                {!isSelf && (
                  <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="destructive"
                        size="default"
                        disabled={remove.isPending}
                      >
                        <Trash2 size={14} />
                        Delete user
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Xoá user {existing?.email}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          User sẽ không login được nữa. Hành động này có thể hoàn tác bằng cách xoá
                          deleted_at trong DB.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Huỷ</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            setError(null);
                            remove.mutate();
                          }}
                        >
                          Xoá
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              <div className="flex gap-2">
                <Link to="/admin/users">
                  <Button type="button" variant="secondary" size="default">
                    Cancel
                  </Button>
                </Link>
                <Button type="submit" variant="default" size="default" disabled={save.isPending}>
                  {save.isPending ? 'Đang lưu…' : 'Save'}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
